import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InlineNotification, NumberInput, Tag, Toggle } from '@carbon/react';
import { api } from '../../api/client';
import { BlockSkeleton, ErrorState, InlineError } from '../../components/states';
import { HE_A_ERRORS } from '../../components/labels';
import type { Command } from '../Commands';

// Bot cho san poker (owner 2026-10-06, backend lib/poker_cho_san.js): chu so huu chon muc cuoc Texas / Omaha nao co bot
// ngoi cho khi ban chua co ai, so ban, so bot moi ban, phut cho; nut ngau nhien chon N muc moi luot trong cac muc da tick.
// Chi chu so huu thay tab nay (API tu choi moi role khac). Chua luu lan nao = gia tri config hien tai cua may game.

type Muc = { bat: boolean; soBan: number; soBot: [number, number]; phutDoi: [number, number] };
type Game = { ngauNhien: boolean; soMucNgauNhien: number; muc: Record<string, Muc> };
type ChoSan = { vgcg_6: Game; vgcg_12: Game };
type GioiHan = Record<string, { dsMuc: number[]; soBanToiDa: number; soBotToiDa: number }>;
type Data = {
  cauHinh: { noi_dung: ChoSan | null; nguon: 'cms' | 'mac_dinh'; phien_ban: number; mac_dinh: ChoSan | null; gioi_han: GioiHan | null } | null;
  loi?: string;
};

const GAME: Array<[keyof ChoSan, string]> = [['vgcg_6', 'Texas'], ['vgcg_12', 'Omaha']];
const PHUT_TOI_DA = 1440;
const so = (v: unknown) => Math.max(1, Math.trunc(Number(v) || 1));

// Moi muc cuoc cua game deu co dong (muc chua co trong noi dung: lay so cua mac dinh, khong tick)
function day(n: ChoSan, md: ChoSan | null, gh: GioiHan): ChoSan {
  const ra = {} as ChoSan;
  for (const [gid] of GAME) {
    const g = n[gid] ?? md?.[gid] ?? { ngauNhien: false, soMucNgauNhien: 1, muc: {} };
    const muc: Record<string, Muc> = {};
    for (const m of gh[gid]?.dsMuc ?? []) {
      const k = String(m);
      muc[k] = g.muc[k] ?? { ...(md?.[gid]?.muc[k] ?? { soBan: 1, soBot: [1, 2], phutDoi: [15, 40] }), bat: false };
    }
    ra[gid] = { ngauNhien: g.ngauNhien, soMucNgauNhien: g.soMucNgauNhien, muc };
  }
  return ra;
}

export function PokerChoSanTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['poker-cho-san'], queryFn: () => api.get<Data>('/api/admin/games/poker/cho-san') });
  const [form, setForm] = useState<ChoSan | null>(null);
  const [xong, setXong] = useState<Command | null>(null);
  const luu = useMutation({
    mutationFn: (f: ChoSan) => api.put<{ command: Command }>('/api/admin/games/poker/cho-san', f),
    onSuccess: (r) => { setXong(r.command); setForm(null); qc.invalidateQueries({ queryKey: ['poker-cho-san'] }); },
  });

  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const ch = q.data.cauHinh;
  if (!ch || !ch.noi_dung || !ch.gioi_han) {
    return <InlineNotification kind="warning" lowContrast hideCloseButton title="Kết nối máy game" subtitle="Hệ A chưa trả lời: chưa đọc được cài đặt bot chờ." />;
  }
  const gh = ch.gioi_han;
  const goc = day(ch.noi_dung, ch.mac_dinh, gh);
  const f = form ?? goc;
  const doi = JSON.stringify(f) !== JSON.stringify(goc);
  const sua = (gid: keyof ChoSan, g: Partial<Game>) => setForm({ ...f, [gid]: { ...f[gid], ...g } });
  const suaMuc = (gid: keyof ChoSan, m: string, x: Partial<Muc>) => {
    const cu = f[gid].muc[m];
    if (cu) sua(gid, { muc: { ...f[gid].muc, [m]: { ...cu, ...x } } });
  };
  const khoang = (cu: [number, number], i: 0 | 1, v: unknown, max: number): [number, number] => {
    const x = Math.min(max, so(v));
    return i === 0 ? [x, Math.max(x, cu[1])] : [Math.min(cu[0], x), x];
  };

  return (
    <form aria-label="Bot chờ sẵn" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1rem' }}
      onSubmit={(e) => { e.preventDefault(); if (doi) luu.mutate(f); }}>
      {xong ? <KetQuaLenh cmd={xong} onClose={() => setXong(null)} /> : null}
      <p className="muted" style={{ margin: 0 }}>
        Bot ngồi chờ sẵn ở bàn chưa có ai (sảnh thấy bàn có người, khách vào là chơi ngay; bot không chơi với nhau). Tick mức cược nào thì
        mức đó có bot chờ: số bàn chờ là các bàn đầu của mức, mỗi bàn ngẫu nhiên trong khoảng số bot, mỗi bot chờ trong khoảng phút rồi đổi
        bot khác. Bật ngẫu nhiên: mỗi lượt máy chọn ngẫu nhiên số mức đã đặt trong các mức đã tick (lượt dài bằng phút chờ lớn nhất của các
        mức được chọn). Bot chỉ ngồi khi Quỹ bot đang bật và đủ tiền mua chip ở mức đó.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Tag type={ch.nguon === 'cms' ? 'blue' : 'gray'}>{ch.nguon === 'cms' ? 'Đang theo CMS' : 'Đang theo mặc định máy game'}</Tag>
      </div>
      {GAME.map(([gid, ten]) => {
        const g = f[gid];
        const l = gh[gid];
        if (!l) return null;
        const soTick = Object.values(g.muc).filter((x) => x.bat).length;
        return (
          <section key={gid} aria-label={'Bot chờ ' + ten} style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>{ten}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', alignItems: 'start', marginBottom: '1rem' }}>
              <Toggle id={`cs-${gid}-nn`} labelText="Ngẫu nhiên" labelA="Tắt" labelB="Bật" toggled={g.ngauNhien}
                onToggle={(v: boolean) => sua(gid, { ngauNhien: v })} />
              <NumberInput id={`cs-${gid}-so`} label="Số mức mỗi lượt" min={1} max={l.dsMuc.length} step={1} value={g.soMucNgauNhien}
                disabled={!g.ngauNhien} helperText={`Đang tick ${soTick} mức`}
                onChange={(_e, { value }) => sua(gid, { soMucNgauNhien: Math.min(l.dsMuc.length, so(value)) })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
              {l.dsMuc.map((mu) => {
                const m = String(mu);
                const x = g.muc[m];
                if (!x) return null;
                const id = `cs-${gid}-${m}`;
                return (
                  <div key={m} data-muc={m} style={{ padding: '0.75rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px', opacity: x.bat ? 1 : 0.7 }}>
                    <Checkbox id={id + '-bat'} labelText={`Mức ${mu.toLocaleString('vi-VN')}: có bot chờ`} checked={x.bat}
                      onChange={(_e, { checked }) => suaMuc(gid, m, { bat: checked })} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <NumberInput id={id + '-ban'} size="sm" label="Số bàn chờ" min={1} max={l.soBanToiDa} step={1} value={x.soBan} disabled={!x.bat}
                        onChange={(_e, { value }) => suaMuc(gid, m, { soBan: Math.min(l.soBanToiDa, so(value)) })} />
                      <span />
                      <NumberInput id={id + '-bot0'} size="sm" label="Bot mỗi bàn từ" min={1} max={l.soBotToiDa} step={1} value={x.soBot[0]} disabled={!x.bat}
                        onChange={(_e, { value }) => suaMuc(gid, m, { soBot: khoang(x.soBot, 0, value, l.soBotToiDa) })} />
                      <NumberInput id={id + '-bot1'} size="sm" label="đến" min={1} max={l.soBotToiDa} step={1} value={x.soBot[1]} disabled={!x.bat}
                        onChange={(_e, { value }) => suaMuc(gid, m, { soBot: khoang(x.soBot, 1, value, l.soBotToiDa) })} />
                      <NumberInput id={id + '-phut0'} size="sm" label="Phút chờ từ" min={1} max={PHUT_TOI_DA} step={1} value={x.phutDoi[0]} disabled={!x.bat}
                        onChange={(_e, { value }) => suaMuc(gid, m, { phutDoi: khoang(x.phutDoi, 0, value, PHUT_TOI_DA) })} />
                      <NumberInput id={id + '-phut1'} size="sm" label="đến" min={1} max={PHUT_TOI_DA} step={1} value={x.phutDoi[1]} disabled={!x.bat}
                        onChange={(_e, { value }) => suaMuc(gid, m, { phutDoi: khoang(x.phutDoi, 1, value, PHUT_TOI_DA) })} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Button type="submit" size="md" disabled={!doi || luu.isPending}>{luu.isPending ? 'Đang lưu...' : 'Lưu'}</Button>
        {doi ? <Button kind="ghost" size="md" onClick={() => setForm(null)}>Bỏ thay đổi</Button> : null}
      </div>
      {luu.error ? <InlineError error={luu.error} /> : null}
    </form>
  );
}

function KetQuaLenh({ cmd, onClose }: { cmd: Command; onClose: () => void }) {
  const ok = cmd.status === 'SUCCEEDED';
  const code = cmd.lastError || (cmd.result && typeof cmd.result.loi === 'string' ? cmd.result.loi : '');
  return (
    <div>
      <InlineNotification kind={ok ? 'success' : 'warning'} lowContrast onClose={() => { onClose(); return false; }}
        title={ok ? 'Lưu bot chờ: xong' : 'Lưu bot chờ: lệnh chưa xong'}
        subtitle={ok ? 'Máy game áp dụng ngay.' : (HE_A_ERRORS[code] ?? (code || cmd.status))} />
      <p><Link className="cell-link" to={'/commands/' + cmd.id}>Xem lệnh</Link></p>
    </div>
  );
}
